import { StartOfQuarter } from 'graphql.types';
import initializeLuxon from './initializeLuxon';
import {
  DateTimeUnit,
  Interval,
  DateTime as LuxonDateTime,
  WeekdayNumbers,
  Info as LuxonInfo
} from 'luxon';

initializeLuxon();

export const DateTime = LuxonDateTime;
export const Info = LuxonInfo;

/*
using preset(https://moment.github.io/luxon/#/formatting?id=presets) is recommended
over using custom format(https://moment.github.io/luxon/#/formatting?id=table-of-tokens)
for human readable date formats
*/

/**********************
Utils for formatting
***********************/
export const dayOnlyDateFormat = 'y-LL-dd';
export const yearMonthFormat = 'y-LL';
export const usaDateFormat = 'LL/dd/y';
export const shortUsaDateFormat = 'LL/dd/y';
// preset: DATE_FULL
export const shortDateMonthYearFormat = 'DD';
export const dateMonthYearFormat = 'DDD';
export const fullDateWithTimeSimple = 'DDD t';
export const dayDateMonthYearFormat = 'DDDD';
// preset: TIME_SIMPLE
export const shortUsaTimeOfDayFormat = 't';
export const usaTimeOfDayFormat = 'tt';
export const solictationDateFormat = "DD 'at' t";
export const shortMonthDateTimeFormat = 'DD t';
export const sessionDateFormat = 'ccc LLL dd';
export const dayDateFormat = 'cccc LLL d';
export const shortMonthDateFormat = 'LLL dd';
export const shortMonthDateWithYearFormat = 'LLL dd yyyy';
export const fullMonthDateFormat = 'LLLL dd';
export const fullMonthDateYearFormat = 'LLLL dd y';
export const dayFormat = 'cccc';
export const dayOfWeekNumberFormat = 'c';
export const historyDateFormat = 'cccc, LLLL d yyyy';
export const historyTimeFormat = 'hh:mm a';
export const shortMonthShortDateFormat = 'LLL d';
export const fullMonthShortDateFormat = 'LLLL d';
export const fullDateAndTimeFormat = 'LLLL d yyyy, t';
export const utcTimeFormat = 't ZZZZ';
export const shortMonthFullDateAndYearFormat = 'LLL dd, y';
export const yearFormat = 'y';

export const formatFullLocalDateTime = (datetime: string) =>
  DateTime.fromISO(datetime).toLocal().toFormat(fullDateAndTimeFormat);
export const formatDayOnlyDate = (datetime: string) =>
  DateTime.fromISO(datetime).toFormat(dayOnlyDateFormat);
export const formatUtcDayOnlyDate = (datetime: string) =>
  DateTime.fromISO(datetime).toUTC().toFormat(dayOnlyDateFormat);
export const formatUsaDate = (datetime: string) =>
  DateTime.fromISO(datetime).toFormat(usaDateFormat);
export const formatIsoString = (datetime: string) =>
  DateTime.fromISO(datetime).toUTC().toISO();
export const formatShortMonthDate = (datetime: string) =>
  DateTime.fromISO(datetime).toFormat(shortMonthDateFormat);
export const formatShortMonthWithYearDate = (datetime: string) =>
  DateTime.fromISO(datetime).toFormat(shortMonthDateWithYearFormat);
export function isDayOnlyDateFormat(date: string) {
  return DateTime.fromFormat(date, dayOnlyDateFormat).isValid;
}

/**********************
Utils for date ranges
***********************/
export type RangeType = {
  id: string;
  range: {
    startDate: LuxonDateTime;
    endDate: LuxonDateTime;
  };
  label: string;
};

const todayRangeBase: RangeType = {
  id: 'today',
  range: {
    startDate: DateTime.now().startOf('day'),
    endDate: DateTime.now().endOf('day'),
  },
  label: 'Today',
};

const yesterdayRangeBase: RangeType = {
  id: 'yesterday',
  range: {
    startDate: DateTime.now().startOf('day').minus({ days: 1 }),
    endDate: DateTime.now().endOf('day').minus({ days: 1 }),
  },
  label: 'Yesterday',
};

export const lastNumberOfDaysRangeBase = (numberOfDays = 7): RangeType => ({
  id: `${numberOfDays}_days`,
  range: {
    startDate: DateTime.now()
      .toUTC()
      .startOf('day')
      .minus({ days: numberOfDays - 1 }),
    endDate: DateTime.now().toUTC().endOf('day'),
  },
  label: `Last ${numberOfDays} days`,
});

const sevenDayRangeBase = lastNumberOfDaysRangeBase(7);
const thirtyDayRangeBase = lastNumberOfDaysRangeBase(30);

export const thisWeekRangeBase = (startOfWeek = 7): RangeType => {
  const today = DateTime.now().toUTC();
  const closestStartOfWeek = DateTime.now()
    .toUTC()
    .set({ weekday: startOfWeek as WeekdayNumbers })
    .startOf('day');
  const startDate =
    today < closestStartOfWeek
      ? closestStartOfWeek.minus({ weeks: 1 })
      : closestStartOfWeek;

  const rangeBase = {
    id: 'this_week',
    range: {
      startDate,
      endDate: startDate.plus({ days: 6 }).endOf('day'),
    },
    label: 'This week',
  };
  return rangeBase;
};

export const thisMonthRangeBase = (): RangeType => ({
  id: 'this_month',
  range: {
    startDate: DateTime.now().toUTC().startOf('month'),
    endDate: DateTime.now().toUTC().endOf('month'),
  },
  label: 'This month',
});

const thisYearRangeBase = (): RangeType => ({
  id: 'this_year',
  range: {
    startDate: DateTime.now().toUTC().startOf('year'),
    endDate: DateTime.now().toUTC().endOf('year'),
  },
  label: 'This year',
});

export const thisQuarterRangeBase = (
  startOfQuarter: StartOfQuarter
): RangeType => {
  const { month, day } = startOfQuarter;
  const thisYearQuarterStart = DateTime.fromFormat(
    `${month}-${day}`,
    'L-d'
  ).toUTC();
  const currentDateTimeUTC = DateTime.now().toUTC();
  const quarterOneStart =
    thisYearQuarterStart > currentDateTimeUTC
      ? thisYearQuarterStart.minus({ years: 1 })
      : thisYearQuarterStart;

  const allQuarters = [...new Array(4)].map((_, idx) => [
    quarterOneStart.plus({ quarters: idx }),
    quarterOneStart.plus({ quarters: idx + 1 }),
  ]);
  const [startDate, endDate] =
    allQuarters.find(([quarter, nextQuarter]) =>
      Interval.fromDateTimes(quarter, nextQuarter).contains(currentDateTimeUTC)
    ) || [];

  return {
    id: 'this_quarter',
    range: {
      startDate: startDate.startOf('day'),
      endDate: endDate.minus({ days: 1 }).endOf('day'),
    },
    label: 'This quarter',
  };
};

const defaultRangeBase = sevenDayRangeBase;

const defaultRangeBases = [
  sevenDayRangeBase,
  thirtyDayRangeBase,
  lastNumberOfDaysRangeBase(90),
  thisWeekRangeBase(),
  thisMonthRangeBase(),
];

export const baseToRange = (rangeBase: RangeType) => ({
  ...rangeBase,
  dayOnlyStartDate: formatDayOnlyDate(rangeBase.range.startDate.toISO() || ''),
  dayOnlyEndDate: formatDayOnlyDate(rangeBase.range.endDate.toISO() || ''),
});

export const todayRange = baseToRange(todayRangeBase);
export const yesterdayRange = baseToRange(yesterdayRangeBase);
export const thirtyDayRange = baseToRange(thirtyDayRangeBase);
export const thisYearRange = baseToRange(thisYearRangeBase());
export const defaultRange = baseToRange(defaultRangeBase);
export const defaultRanges = defaultRangeBases.map(baseToRange);

const rangeBases = [
  todayRangeBase,
  yesterdayRangeBase,
  ...defaultRangeBases,
  thisYearRangeBase(),
];
export const ranges = rangeBases.map(baseToRange);

export const getPreviousPeriod = (rawStartDate: string, rawEndDate: string) => {
  // Convert the dates into UTC to prevent dealing with DST behavior
  // And setting middle of the day to avoid date change if close to the midnight
  if (!rawStartDate || !rawEndDate) return [];

  const startDate = DateTime.fromISO(rawStartDate).set({ hour: 12 }).toUTC();
  const endDate = DateTime.fromISO(rawEndDate).set({ hour: 12 }).toUTC();

  const diff = endDate.diff(startDate);
  const previousEndDate = startDate.minus({ days: 1 });
  const previousStartDate = previousEndDate.minus(diff);

  return [
    previousStartDate.toFormat(dayOnlyDateFormat),
    previousEndDate.toFormat(dayOnlyDateFormat),
  ];
};

export const getSamePeriodLastYear = (
  rawStartDate: string,
  rawEndDate: string
) => {
  if (!rawStartDate || !rawEndDate) return [];

  const startDate = DateTime.fromISO(rawStartDate).set({ hour: 12 }).toUTC();
  const endDate = DateTime.fromISO(rawEndDate).set({ hour: 12 }).toUTC();

  const previousStartDate = startDate.minus({ years: 1 });
  const previousEndDate = endDate.minus({ years: 1 });

  return [
    previousStartDate.toFormat(dayOnlyDateFormat),
    previousEndDate.toFormat(dayOnlyDateFormat),
  ];
};

export const getPreviousPeriodByType = (
  rawStartDate: string,
  periodType: DateTimeUnit
) => {
  if (!rawStartDate) return [];

  const startDate = DateTime.fromISO(rawStartDate)
    .startOf('day')
    .set({ hour: 12 })
    .toUTC();

  const previousEndDateObj = startDate.minus({ days: 1 });
  const previousEndDate = previousEndDateObj.toFormat(dayOnlyDateFormat);
  const previousStartDate = previousEndDateObj
    .startOf(periodType)
    .toFormat(dayOnlyDateFormat);

  return [previousStartDate, previousEndDate];
};

export const getPreviousMonthDates = (date: string) =>
  getPreviousPeriodByType(date, 'month');

export const getPreviousYearDates = (date: string) =>
  getPreviousPeriodByType(date, 'year');

export const lastWeekRangeBase = (startOfWeek: number) => {
  const {
    range: { startDate, endDate },
  } = thisWeekRangeBase(startOfWeek);

  return {
    id: 'last_week',
    range: {
      startDate: startDate.minus({ weeks: 1 }),
      endDate: endDate.minus({ weeks: 1 }),
    },
    label: 'Last week',
  };
};

export const lastMonthRangeBase = () => ({
  id: 'last_month',
  range: {
    startDate: DateTime.now().toUTC().minus({ months: 1 }).startOf('month'),
    endDate: DateTime.now().toUTC().minus({ months: 1 }).endOf('month'),
  },
  label: 'Last month',
});

export const lastQuarterRangeBase = (startOfQuarter: StartOfQuarter) => {
  const {
    range: { startDate, endDate },
  } = thisQuarterRangeBase(startOfQuarter);

  return {
    id: 'last_quarter',
    range: {
      startDate: startDate.minus({ quarters: 1 }),
      endDate: endDate.minus({ quarters: 1 }),
    },
    label: 'Last quarter',
  };
};

export const lastYearRangeBase = () => {
  const {
    range: { startDate, endDate },
  } = thisYearRangeBase();

  return {
    id: 'last_year',
    range: {
      startDate: startDate.minus({ years: 1 }),
      endDate: endDate.minus({ years: 1 }),
    },
    label: 'Last year',
  };
};

export const monthStrings = Info.months();

export const weekdayStrings = Info.weekdays().map(weekday =>
  weekday.toUpperCase()
);

export const weekdayStringsSundayStart = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];

export const weekdayStringToNumber = (weekday: string) =>
  weekdayStrings.indexOf(weekday.toUpperCase()) + 1;

export const weeklyRangeBases = {
  THIS_WEEK: (startOfWeek: string) =>
    thisWeekRangeBase(weekdayStringToNumber(startOfWeek)),
  LAST_WEEK: (startOfWeek: string) =>
    lastWeekRangeBase(weekdayStringToNumber(startOfWeek)),
};

export const quarterlyRangeBases = {
  THIS_QUARTER: (startOfQuarter: StartOfQuarter) =>
    thisQuarterRangeBase(startOfQuarter),
  LAST_QUARTER: (startOfQuarter: StartOfQuarter) =>
    lastQuarterRangeBase(startOfQuarter),
};

export const allRangesBases = {
  TODAY: todayRangeBase,
  YESTERDAY: yesterdayRangeBase,
  THIS_MONTH: thisMonthRangeBase(),
  THIS_YEAR: thisYearRangeBase(),
  LAST_3_DAYS: lastNumberOfDaysRangeBase(3),
  LAST_5_DAYS: lastNumberOfDaysRangeBase(5),
  LAST_7_DAYS: lastNumberOfDaysRangeBase(7),
  LAST_14_DAYS: lastNumberOfDaysRangeBase(14),
  LAST_30_DAYS: lastNumberOfDaysRangeBase(30),
  LAST_60_DAYS: lastNumberOfDaysRangeBase(60),
  LAST_90_DAYS: lastNumberOfDaysRangeBase(90),
  LAST_MONTH: lastMonthRangeBase(),
  LAST_YEAR: lastYearRangeBase(),
};

export const rangeIdEnums = {
  '3_days': 'LAST_3_DAYS',
  '5_days': 'LAST_5_DAYS',
  '7_days': 'LAST_7_DAYS',
  '14_days': 'LAST_14_DAYS',
  '30_days': 'LAST_30_DAYS',
  '60_days': 'LAST_60_DAYS',
  '90_days': 'LAST_90_DAYS',
  last_month: 'LAST_MONTH',
  last_quarter: 'LAST_QUARTER',
  last_week: 'LAST_WEEK',
  last_year: 'LAST_YEAR',
  this_month: 'THIS_MONTH',
  this_quarter: 'THIS_QUARTER',
  this_week: 'THIS_WEEK',
  this_year: 'THIS_YEAR',
  today: 'TODAY',
  yesterday: 'YESTERDAY',
};

export const EnumRangeIds = {
  LAST_3_DAYS: '3_days',
  LAST_5_DAYS: '5_days',
  LAST_7_DAYS: '7_days',
  LAST_14_DAYS: '14_days',
  LAST_30_DAYS: '30_days',
  LAST_60_DAYS: '60_days',
  LAST_90_DAYS: '90_days',
  LAST_MONTH: 'last_month',
  LAST_QUARTER: 'last_quarter',
  LAST_WEEK: 'last_week',
  LAST_YEAR: 'last_year',
  THIS_MONTH: 'this_month',
  THIS_QUARTER: 'this_quarter',
  THIS_WEEK: 'this_week',
  THIS_YEAR: 'this_year',
  TODAY: 'today',
  YESTERDAY: 'yesterday',
};

export const nextDay = DateTime.now()
  .endOf('day')
  .toUTC()
  .plus({ days: 1 })
  .toFormat(dayOnlyDateFormat);

/**********************
Helper methods
***********************/
export const isDateTimeInstance = (obj: unknown) =>
  obj instanceof LuxonDateTime;

export const toDateTimeObj = (date: LuxonDateTime | string): LuxonDateTime =>
  (isDateTimeInstance(date) ? date : DateTime.fromISO(date));

export const diffInDays = (from: LuxonDateTime, diffTo: LuxonDateTime) => {
  const startOfFrom = from.startOf('day');
  const startOfTo = diffTo.startOf('day');
  return Math.floor(
    startOfFrom.diff(startOfTo).milliseconds / (1000 * 60 * 60 * 24)
  );
};

export const weeksToDays = (from: LuxonDateTime, weeks: number): number => {
  const weeksBefore = from.minus({ weeks });
  return diffInDays(from, weeksBefore);
};

export const monthsToDays = (from: LuxonDateTime, months: number): number => {
  const monthsBefore = from.minus({ months });
  return diffInDays(from, monthsBefore);
};

export const yearsToDays = (from: LuxonDateTime, years: number): number => {
  const yearsBefore = from.minus({ years });
  return diffInDays(from, yearsBefore);
};

export const subtract = (
  from: LuxonDateTime | string,
  amount: number,
  unit: string
): string =>
  toDateTimeObj(from)
    .minus({ [unit]: amount })
    .toFormat(dayOnlyDateFormat);

export const getDurationInDays = (
  startDate: LuxonDateTime | string,
  endDate: LuxonDateTime | string
) => {
  return diffInDays(toDateTimeObj(endDate), toDateTimeObj(startDate)) + 1;
};

export const timezones = {
  utc: 'utc',
  local: 'local',
};

export const formatDate = (
  date: LuxonDateTime,
  format: string,
  timezone = timezones.local
) => {
  const dateWithTz =
    timezone === timezones.local ? date.toLocal() : date.toUTC();
  if (dateWithTz.year !== DateTime.now().year) {
    return dateWithTz.toFormat(`${format} yyyy`);
  }
  return dateWithTz.toFormat(format);
};

export const formatTime = (date: LuxonDateTime) =>
  date.toUTC().toFormat('t ZZZZ');

export const formatDateWithTime = (
  date: LuxonDateTime,
  format: string,
  timezone = timezones.local
) => {
  const dateWithTz =
    timezone === timezones.local ? date.toLocal() : date.toUTC();
  const utcTime = formatTime(dateWithTz);
  const formatWithTime =
    timezone === timezones.local ? format : `${format} 'time'`;
  return formatDate(date, formatWithTime, timezone).replace('time', utcTime);
};

export const isBeforeThanNow = (date: LuxonDateTime) => {
  return date <= DateTime.now();
};

export const getDaysInMonth = (month?: number) => {
  if (!month) return 0;
  return DateTime.fromObject({ month }).daysInMonth;
};
